import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cycleFeedbackKeys } from "@/lib/query";

async function failure(res: Response, fallback: string): Promise<Error> {
  const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new Error(typeof json?.error === "string" && json.error ? json.error : fallback);
}

export type TriageStatus = "unreviewed" | "reviewed" | "needs_follow_up";

export interface CycleFeedbackFilters {
  sentimentKey?: string;
  competencyId?: string;
  roleId?: string;
  roleLevelId?: string;
  relationshipKey?: string;
  reviewStatusKey?: string;
  triageStatus?: TriageStatus;
}

export interface CycleFeedbackRow {
  id: string;
  body: string;
  sentimentKey: string;
  sentimentName: string;
  isAnonymous: boolean;
  createdAt: string;
  subjectPersonId: string;
  subjectName: string;
  roleId: string;
  roleName: string;
  roleLevelId: string | null;
  roleLevelName: string | null;
  reviewStatusKey: string;
  relationshipKey: string | null;
  relationshipName: string | null;
  criterionId: string | null;
  criterionTitle: string | null;
  competencyId: string | null;
  competencyName: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  triageStatus: TriageStatus;
  triagedAt: string | null;
}

function filterParams(filters: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `&${qs}` : "";
}

/** WI-270/271 — every piece of written feedback in a cycle, filtered down. */
export function useCycleFeedback(cycleId: string | null, filters: CycleFeedbackFilters) {
  return useQuery({
    queryKey: cycleFeedbackKeys.list(cycleId ?? "", { ...filters }),
    queryFn: async (): Promise<CycleFeedbackRow[]> => {
      const res = await fetch(
        `/api/reports/cycle-feedback?cycleId=${cycleId}${filterParams(filters)}`,
      );
      if (!res.ok) throw await failure(res, "Failed to load feedback for this round");
      return (await res.json()).data;
    },
    enabled: Boolean(cycleId),
  });
}

export interface AdHocFeedbackReportFilters {
  sentimentKey?: string;
  triageStatus?: TriageStatus;
  kind?: "project" | "general";
}

export interface AdHocFeedbackReportRow {
  id: string;
  body: string;
  sentimentKey: string;
  sentimentName: string;
  isAnonymous: boolean;
  createdAt: string;
  subjectPersonId: string;
  subjectName: string;
  authorName: string | null;
  relatedProjectName: string | null;
  isVisibleToEmployee: boolean;
  triageStatus: TriageStatus;
  triagedAt: string | null;
}

/** HR's view of feedback given outside any round (ad hoc and post-project). */
export function useAdHocFeedbackReport(filters: AdHocFeedbackReportFilters) {
  return useQuery({
    queryKey: cycleFeedbackKeys.adHoc({ ...filters }),
    queryFn: async (): Promise<AdHocFeedbackReportRow[]> => {
      const qs = filterParams(filters).replace(/^&/, "");
      const res = await fetch(`/api/reports/ad-hoc-feedback${qs ? `?${qs}` : ""}`);
      if (!res.ok) throw await failure(res, "Failed to load feedback given outside a round");
      return (await res.json()).data;
    },
  });
}

export interface SentimentCount {
  sentimentKey: string;
  sentimentName: string;
  count: number;
}

export interface SentimentBreakdownGroup {
  groupId: string | null;
  groupLabel: string;
  counts: SentimentCount[];
}

export interface CompetencyNegativeCount {
  competencyId: string;
  competencyName: string;
  categoryName: string | null;
  categoryColor: string | null;
  negativeCount: number;
}

export interface CycleFeedbackThemes {
  overall: SentimentCount[];
  byRole: SentimentBreakdownGroup[];
  byLevel: SentimentBreakdownGroup[];
  negativeByCompetency: CompetencyNegativeCount[];
}

/** WI-272 — the shape of a cycle's feedback at a glance. */
export function useCycleFeedbackThemes(cycleId: string | null) {
  return useQuery({
    queryKey: cycleFeedbackKeys.themes(cycleId ?? ""),
    queryFn: async (): Promise<CycleFeedbackThemes> => {
      const res = await fetch(`/api/reports/cycle-feedback/themes?cycleId=${cycleId}`);
      if (!res.ok) throw await failure(res, "Failed to load this round's feedback themes");
      return (await res.json()).data;
    },
    enabled: Boolean(cycleId),
  });
}

/** WI-273 — mark one piece of feedback reviewed or needing follow-up. */
export function useSetFeedbackTriage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ feedbackId, status }: { feedbackId: string; status: TriageStatus }) => {
      const res = await fetch(`/api/review-feedback/${feedbackId}/triage`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw await failure(res, "Failed to update the triage state");
      return (await res.json()).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cycleFeedbackKeys.all });
      toast.success("Triage state updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
